/** /otas/ — global Online_Travel_Agency catalog (list + CRUD) + the email-mapping
 *  preview and the spreadsheet import (preview + start).
 *
 *  An OTA is a named source of bookings with an ordered set of email-domain rules
 *  used to attribute an inbound email to the agency. OTAs are GLOBAL — there is no
 *  owning tour operator — so nothing here takes a tenant.
 *
 *  Hand-authored wrapper (console convention: never depend on generated types for
 *  feature modules). Online_Travel_Agency has no optimistic-concurrency token, so
 *  writes are last-writer-wins (no conflict banner, like regions).
 */
import { apiDelete, apiGet, apiPatch, apiPost, client } from './client';
import type { PaginatedResponse } from './users';

/** Domain match strategy (`match_type`), in the numeric order the backend uses. */
export const MATCH_TYPES: { value: 0 | 1 | 2 | 3; label: string }[] = [
  { value: 0, label: 'Ends with' },
  { value: 1, label: 'Exact' },
  { value: 2, label: 'Starts with' },
  { value: 3, label: 'Contains' },
];

/** One email-domain rule under an OTA. Per-domain override columns are nullable:
 *  null → inherit the OTA's value. */
export interface OTADomain {
  /** On write: omit / null to create a new row. */
  id: number | null;
  domain: string;
  match_type: 0 | 1 | 2 | 3;
  match_type_display?: string;
  sequence?: number;
  /** Override; null → inherit the OTA's value. */
  unique_emails?: boolean | null;
  /** Override; null → inherit the OTA's value. */
  plus_supported?: boolean | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  suppress_emails?: boolean;
}

export interface OTAListItem {
  id: number;
  name: string;
  enabled: boolean;
  sequence: number;
  unique_emails?: boolean;
  plus_supported?: boolean;
  direct?: boolean;
  app_promotion?: boolean;
  review_url?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  domain_count?: number;
  /** Derived summary: number of channels this OTA enforces a format for. The
   *  raw {channel: format} map is detail-only (see `OTADetail.format_overrides`). */
  format_overrides_count?: number;
}

/** Channels a format override may target (Notification_Block.CHANNELS). */
export const FORMAT_OVERRIDE_CHANNELS = ['email', 'sms', 'whatsapp', 'chat', 'airbnb'] as const;
export type FormatOverrideChannel = (typeof FORMAT_OVERRIDE_CHANNELS)[number];

/** Formats an OTA can enforce for a channel. */
export const ENFORCED_FORMATS = ['html', 'markdown', 'plaintext'] as const;
export type EnforcedFormat = (typeof ENFORCED_FORMATS)[number];

/** Per-channel rendering format the OTA enforces on messages it relays (e.g.
 *  Viator strips HTML → {email: 'plaintext'}). Wins over explicit rendering
 *  hints during notification delivery. Empty object = no enforcement. */
export type OTAFormatOverrides = Partial<Record<FormatOverrideChannel, EnforcedFormat>>;

export interface OTADetail extends OTAListItem {
  notes?: string | null;
  format_overrides?: OTAFormatOverrides;
  domains?: OTADomain[];
}

/** Create/update body. `name` required on create (unique). `domains`, when
 *  present, is a full-array upsert: rows with an `id` update, rows without create,
 *  and existing rows absent from the array are deleted. Omit `domains` entirely to
 *  leave them untouched. */
export interface OTAWrite {
  name?: string;
  enabled?: boolean;
  sequence?: number;
  unique_emails?: boolean;
  plus_supported?: boolean;
  direct?: boolean;
  app_promotion?: boolean;
  review_url?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  notes?: string | null;
  format_overrides?: OTAFormatOverrides;
  domains?: OTADomain[];
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  /** Matches the OTA name OR any of its domains. */
  search?: string;
  enabled?: boolean;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.enabled !== undefined) usp.set('enabled', String(params.enabled));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<OTAListItem>> {
  return apiGet<PaginatedResponse<OTAListItem>>(`/otas/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<OTADetail> {
  return apiGet<OTADetail>(`/otas/${id}/`);
}

export function create(body: OTAWrite): Promise<OTADetail> {
  return apiPost<OTADetail>('/otas/', body);
}

export function update(id: number, body: OTAWrite): Promise<OTADetail> {
  return apiPatch<OTADetail>(`/otas/${id}/`, body);
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/otas/${id}/`);
}

// ── Email-mapping preview ──────────────────────────────────────────────────
// Informational: which OTA (and which domain rule) an address maps to, and the
// effective settings once overrides are applied. No persistence.

export interface EmailPreviewResult {
  matched: boolean;
  email: string;
  ota?: { id: number; name: string | null };
  domain?: {
    id?: number;
    domain?: string;
    match_type?: number;
    match_type_display?: string;
  } | null;
  effective?: {
    unique_emails?: boolean;
    plus_supported?: boolean;
    contact_email?: string | null;
    contact_phone?: string | null;
  } | null;
}

export function emailPreview(email: string): Promise<EmailPreviewResult> {
  return apiGet<EmailPreviewResult>(`/otas/email_preview/?email=${encodeURIComponent(email)}`);
}

// ── Spreadsheet import (preview → start) ───────────────────────────────────
// Two-step, server-driven: preview parses + caches the sheet and returns the
// columns, sample rows, an autodetected format+mapping, and the catalog of
// selectable formats (each with its logical fields). `cache_key` is then handed
// to start along with the chosen format + column mapping.

export interface ImportFormatField {
  key: string;
  label: string;
  required: boolean;
}

export interface ImportFormat {
  label: string;
  fields: ImportFormatField[];
}

export interface ImportPreviewResult {
  cache_key: string;
  columns: string[];
  preview: string[][];
  row_count: number;
  source?: { kind?: string; name?: string } & Record<string, unknown>;
  autodetect?: {
    format?: string | null;
    mapping?: Record<string, string | null>;
  };
  formats: Record<string, ImportFormat>;
}

/** Preview an upload (CSV/XLSX `file`) OR a Google Sheets URL. Multipart when a
 *  file is given; plain JSON `{ sheet_url }` otherwise. */
export async function importPreview(input: {
  file?: File;
  sheetUrl?: string;
}): Promise<ImportPreviewResult> {
  if (input.file) {
    const fd = new FormData();
    fd.append('file', input.file);
    const r = await client.post<ImportPreviewResult>('/ops/ota_import/preview/', fd, {
      headers: { 'Content-Type': undefined },
    });
    return r.data;
  }
  return apiPost<ImportPreviewResult>('/ops/ota_import/preview/', {
    sheet_url: input.sheetUrl,
  });
}

export interface ImportStartPayload {
  cache_key: string;
  format: string;
  mapping: Record<string, string>;
  dry_run: boolean;
  /** OTAs are global; leave null (the default console scope). */
  tour_operator_id?: number | null;
}

export interface ImportStartResult {
  /** Background-task uuid. */
  id: string;
  state?: number;
  tour_operator_id?: number | null;
}

export function importStart(payload: ImportStartPayload): Promise<ImportStartResult> {
  return apiPost<ImportStartResult>('/ops/ota_import/start/', payload);
}
